import type {
  RdHoursBookedVisit,
  RdHoursResponse,
  RdHoursSaveRequest,
  RdHoursWeekResponse,
  RdHoursWeekSaveRequest,
  RdHoursWeekdayName,
} from "../../../shared/api-types";
import {
  RD_HOURS_DAYS,
  blocksFromBody,
  blocksToBody,
  clockTime,
  emptyWeek,
  isVisitInside,
  weekFrom,
  type HoursWeek,
} from "./rd-hours-editor";

// The Availability view's API edge (NO-878): request paths and bodies, the
// closed error kinds and their copy, dates, time zones, booked visits, and the
// page's gate and heading. Pure, relative imports only, so `tsx --test` runs
// it. Client names stay in memory: nothing here logs, stores or encodes one.

// --- Requests ---------------------------------------------------------------

function rdQuery(rdId: string | undefined): string {
  return rdId === undefined ? "" : `?rdId=${encodeURIComponent(rdId)}`;
}

export function rdHoursPath(rdId: string | undefined): string {
  return `/api/rd-hours${rdQuery(rdId)}`;
}

export function rdHoursWeekPath(weekStart: string, rdId: string | undefined): string {
  return `/api/rd-hours/weeks/${encodeURIComponent(weekStart)}${rdQuery(rdId)}`;
}

export function rdHoursQueryKey(rdId: string | undefined): readonly unknown[] {
  return ["rd-hours", rdId ?? null];
}

export function rdHoursWeekQueryKey(
  rdId: string | undefined,
  weekStart: string
): readonly unknown[] {
  return ["rd-hours-week", rdId ?? null, weekStart];
}

/** Every week: all seven days (an empty day is off) and the zone. */
export function everyWeekSaveBody(input: {
  version: number;
  timeZone: string;
  week: HoursWeek;
}): RdHoursSaveRequest {
  const days = Object.fromEntries(
    RD_HOURS_DAYS.map((day) => [day, blocksToBody(input.week[day])])
  );

  return { baseVersion: input.version, timeZone: input.timeZone, days };
}

/** One week: only the days the change replaces; an empty one is off that week. */
export function weekSaveBody(input: {
  version: number;
  week: HoursWeek;
  changed: readonly RdHoursWeekdayName[];
}): RdHoursWeekSaveRequest {
  const days = Object.fromEntries(
    RD_HOURS_DAYS.filter((day) => input.changed.includes(day)).map((day) => [
      day,
      blocksToBody(input.week[day]),
    ])
  );

  return { baseVersion: input.version, days };
}

// --- What the editor starts from ---------------------------------------------

export interface DraftSource {
  days: HoursWeek;
  /** One week: the days the stored change replaces. */
  changed: RdHoursWeekdayName[];
  version: number;
  timeZone: string;
}

export function templateSource(response: RdHoursResponse): DraftSource {
  const template = response.template;
  const days =
    template === null
      ? emptyWeek()
      : weekFrom((day) => blocksFromBody(template.days[day]));

  return { days, changed: [], version: response.version, timeZone: response.timeZone };
}

export function weekSource(
  response: Pick<RdHoursWeekResponse, "days" | "change" | "timeZone">
): DraftSource {
  return {
    days: weekFrom((day) => blocksFromBody(response.days[day].blocks)),
    changed: RD_HOURS_DAYS.filter((day) => response.days[day].changed),
    version: response.change.version,
    timeZone: response.timeZone,
  };
}

// --- Errors -----------------------------------------------------------------

export type RdHoursErrorKind =
  | { kind: "version_conflict"; current: unknown }
  | { kind: "break_required"; days: RdHoursWeekdayName[]; message: string }
  | { kind: "week_ended" }
  | { kind: "invalid_time_zone" }
  | { kind: "rd_tombstoned" }
  | { kind: "forbidden" }
  | { kind: "unavailable" }
  | { kind: "failed" };

function readBody(error: unknown): {
  status: number | null;
  body: Record<string, unknown>;
} {
  if (typeof error !== "object" || error === null) return { status: null, body: {} };

  const { status, body } = error as { status?: unknown; body?: unknown };
  const isBody = typeof body === "object" && body !== null;

  return {
    status: typeof status === "number" ? status : null,
    body: isBody ? (body as Record<string, unknown>) : {},
  };
}

function conflictKind(body: Record<string, unknown>): RdHoursErrorKind {
  if (body.code === "rd_tombstoned") return { kind: "rd_tombstoned" };

  return body.code === "version_conflict"
    ? { kind: "version_conflict", current: body.current }
    : { kind: "failed" };
}

function badRequestKind(body: Record<string, unknown>): RdHoursErrorKind {
  if (body.code === "week_ended") return { kind: "week_ended" };

  return body.code === "invalid_time_zone"
    ? { kind: "invalid_time_zone" }
    : { kind: "failed" };
}

function breakKind(body: Record<string, unknown>): RdHoursErrorKind {
  const days = Array.isArray(body.days) ? (body.days as RdHoursWeekdayName[]) : [];
  const message = typeof body.message === "string" ? body.message : "";

  return { kind: "break_required", days, message };
}

/** A failed read or save as one closed kind. Anything unexpected is `failed`. */
export function classifyRdHoursError(error: unknown): RdHoursErrorKind {
  const { status, body } = readBody(error);

  switch (status) {
    case 400:
      return badRequestKind(body);
    case 403:
      return { kind: "forbidden" };
    case 404:
      return { kind: "unavailable" };
    case 409:
      return conflictKind(body);
    case 422:
      return breakKind(body);
    default:
      return { kind: "failed" };
  }
}

export const RD_HOURS_COPY = {
  conflict:
    "Someone else changed these hours while you were editing. Your changes were not saved.",
  loadLatest: "Load the latest hours",
  weekEnded: "That week has ended. Pick a later week.",
  invalidTimeZone: "Pick a time zone from the list.",
  tombstoned: "This RD is no longer active, so their hours can't be changed.",
  forbidden: "You can't change this RD's hours.",
  unavailable: "Hours aren't available for this RD yet.",
  saveFailed: "Your hours weren't saved. Try again.",
  saved: "Saved",
  noSelectionTitle: "Choose an RD to see their hours",
  noSelectionBody: "Nothing loads until you select an RD.",
  zoneInWeek: "Change your time zone under Every week.",
} as const;

/** What a failed save says, inline beside Save. */
export function saveErrorCopy(error: RdHoursErrorKind): string {
  switch (error.kind) {
    case "version_conflict":
      return RD_HOURS_COPY.conflict;
    case "break_required":
      return error.message;
    case "week_ended":
      return RD_HOURS_COPY.weekEnded;
    case "invalid_time_zone":
      return RD_HOURS_COPY.invalidTimeZone;
    case "rd_tombstoned":
      return RD_HOURS_COPY.tombstoned;
    case "forbidden":
      return RD_HOURS_COPY.forbidden;
    case "unavailable":
      return RD_HOURS_COPY.unavailable;
    default:
      return RD_HOURS_COPY.saveFailed;
  }
}

/** A refusal no retry can change: Save stays off for this draft. */
export function isTerminalSaveError(error: RdHoursErrorKind | null): boolean {
  if (error === null) return false;

  return ["rd_tombstoned", "forbidden", "unavailable"].includes(error.kind);
}

// --- Dates ------------------------------------------------------------------

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function isoToUtcMs(date: string): number {
  const [year, month, day] = date.split("-").map(Number);

  return Date.UTC(year!, month! - 1, day!);
}

export function addIsoDays(date: string, days: number): string {
  return new Date(isoToUtcMs(date) + days * MS_PER_DAY).toISOString().slice(0, 10);
}

function utcFormat(date: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-US", { ...options, timeZone: "UTC" }).format(
    new Date(isoToUtcMs(date))
  );
}

/** "Nov 3" */
export function monthDayLabel(date: string): string {
  return utcFormat(date, { month: "short", day: "numeric" });
}

/** "Mon, Nov 2" */
export function weekdayDateLabel(date: string): string {
  return utcFormat(date, { weekday: "short", month: "short", day: "numeric" });
}

/** "Tue, Nov 3": a day of the week that starts on `weekStart`. */
export function dayOfWeekLabel(weekStart: string, day: RdHoursWeekdayName): string {
  return weekdayDateLabel(addIsoDays(weekStart, RD_HOURS_DAYS.indexOf(day)));
}

/** "Nov 3": a day's date in the week that starts on `weekStart`. */
export function dayDateLabel(weekStart: string, day: RdHoursWeekdayName): string {
  return monthDayLabel(addIsoDays(weekStart, RD_HOURS_DAYS.indexOf(day)));
}

function yearOf(date: string): string {
  return date.slice(0, 4);
}

/** "Nov 2 to 8", "Oct 26 to Nov 1"; with the year, "Nov 2 to 8, 2026". */
export function weekRangeLabel(weekStart: string, withYear: boolean): string {
  const weekEnd = addIsoDays(weekStart, 6);
  const sameMonth = weekStart.slice(0, 7) === weekEnd.slice(0, 7);
  const endLabel = sameMonth
    ? String(Number(weekEnd.slice(8)))
    : monthDayLabel(weekEnd);

  if (!withYear) return `${monthDayLabel(weekStart)} to ${endLabel}`;

  if (yearOf(weekStart) !== yearOf(weekEnd)) {
    return `${monthDayLabel(weekStart)}, ${yearOf(weekStart)} to ${endLabel}, ${yearOf(weekEnd)}`;
  }

  return `${monthDayLabel(weekStart)} to ${endLabel}, ${yearOf(weekEnd)}`;
}

// --- Time zones -------------------------------------------------------------

export interface TimeZoneOption {
  value: string;
  label: string;
}

export const RD_HOURS_TIME_ZONES: readonly TimeZoneOption[] = [
  { value: "America/New_York", label: "Eastern time" },
  { value: "America/Chicago", label: "Central time" },
  { value: "America/Denver", label: "Mountain time" },
  { value: "America/Phoenix", label: "Arizona time" },
  { value: "America/Los_Angeles", label: "Pacific time" },
  { value: "America/Anchorage", label: "Alaska time" },
  { value: "Pacific/Honolulu", label: "Hawaii time" },
];

export function timeZoneLabel(zone: string): string {
  return RD_HOURS_TIME_ZONES.find((option) => option.value === zone)?.label ?? zone;
}

/** The US zones, plus a stored zone outside them so it still reads as chosen. */
export function timeZoneOptions(current: string): readonly TimeZoneOption[] {
  const listed = RD_HOURS_TIME_ZONES.some((option) => option.value === current);

  return listed
    ? RD_HOURS_TIME_ZONES
    : [...RD_HOURS_TIME_ZONES, { value: current, label: current }];
}

// --- Booked visits ----------------------------------------------------------

export interface WeekVisit {
  id: string;
  start: number;
  end: number;
  /** As stored; "Client" when the record has no name. */
  name: string;
  /** The server's answer for the saved hours. */
  outsideHours: boolean;
}

export type WeekVisits = Record<RdHoursWeekdayName, WeekVisit[]>;

const formatters = new Map<string, Intl.DateTimeFormat>();

function zoneFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);

  if (formatter === undefined) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
    formatters.set(timeZone, formatter);
  }

  return formatter;
}

/** The zone's wall-clock date and minute of the day at an instant. */
export function localDateAndMinute(
  instant: string,
  timeZone: string
): { date: string; minute: number } {
  const parts = Object.fromEntries(
    zoneFormatter(timeZone)
      .formatToParts(new Date(instant))
      .map(({ type, value }) => [type, value])
  );

  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    minute: Number(parts.hour) * 60 + Number(parts.minute),
  };
}

function dayOffset(from: string, to: string): number {
  return Math.round((isoToUtcMs(to) - isoToUtcMs(from)) / MS_PER_DAY);
}

/** The week's visits by local day, in start order; a visit ending past midnight runs over 1440. */
export function weekVisits(
  visits: readonly RdHoursBookedVisit[],
  weekStart: string,
  timeZone: string
): WeekVisits {
  const byDay = Object.fromEntries(
    RD_HOURS_DAYS.map((day) => [day, [] as WeekVisit[]])
  ) as WeekVisits;

  for (const visit of visits) {
    const start = localDateAndMinute(visit.startsAt, timeZone);
    const end = localDateAndMinute(visit.endsAt, timeZone);
    const day = RD_HOURS_DAYS[dayOffset(weekStart, start.date)];

    if (day === undefined) continue;

    byDay[day].push({
      id: visit.appointmentId,
      start: start.minute,
      end: end.minute + dayOffset(start.date, end.date) * 24 * 60,
      name: visit.clientName ?? "Client",
      outsideHours: visit.outsideHours,
    });
  }

  for (const day of RD_HOURS_DAYS) byDay[day].sort((a, b) => a.start - b.start);

  return byDay;
}

/**
 * Whether each visit is outside the hours on screen: the server's answer for
 * a day as saved, the client's restated rule for a day edited since.
 */
export function visitIsOutside(
  visit: WeekVisit,
  week: HoursWeek,
  day: RdHoursWeekdayName,
  edited: boolean
): boolean {
  return edited
    ? !isVisitInside(week[day], visit.start, visit.end)
    : visit.outsideHours;
}

export interface OutsideVisits {
  count: number;
  /** "Nov 3: Rosa G. at 9:30 AM, Hannah W. at 12:00 PM", one per day. */
  lines: string[];
}

export function outsideVisits(input: {
  visits: WeekVisits;
  week: HoursWeek;
  edited: readonly RdHoursWeekdayName[];
  weekStart: string;
}): OutsideVisits {
  let count = 0;
  const lines: string[] = [];

  for (const day of RD_HOURS_DAYS) {
    const edited = input.edited.includes(day);
    const outside = input.visits[day].filter((visit) =>
      visitIsOutside(visit, input.week, day, edited)
    );

    if (outside.length === 0) continue;

    count += outside.length;
    const who = outside.map(
      (visit) => `${visit.name} at ${clockTime(visit.start, true)}`
    );
    lines.push(`${dayDateLabel(input.weekStart, day)}: ${who.join(", ")}`);
  }

  return { count, lines };
}

/** "1 visit is" / "3 visits are" outside these hours. */
export function outsideVisitsWord(count: number): string {
  return count === 1 ? "visit is" : "visits are";
}

// --- Page gate and heading ----------------------------------------------------

export type AvailabilityGate = "blank" | "closed" | "open";

/**
 * Availability exists only in the single-rail frame, with `rd-schedule` on,
 * for a viewer who manages some RD's hours. Blank while flags and the viewer
 * load, so the tab never flashes; closed renders the router's 404.
 */
export function availabilityGate(input: {
  frame: "rail" | "classic";
  loading: boolean;
  rdScheduleEnabled: boolean;
  canManageHours: boolean;
}): AvailabilityGate {
  if (input.frame !== "rail") return "closed";

  if (input.loading) return "blank";

  return input.rdScheduleEnabled && input.canManageHours ? "open" : "closed";
}

export type AvailabilityHeading = "greeting" | "selector" | "named";

/**
 * Whose hours the view shows and how the h1 names them: the viewer's greeting
 * for their own, the scope selector for a viewer who can pick an RD, the RD's
 * name for a supervisor on `?rdId=`. An admin with no RD picked and no hours
 * of their own loads nothing.
 */
export function availabilityHeading(input: {
  canSelectRd: boolean;
  rdId: string | undefined;
  holdsOwnHours: boolean;
}): { heading: AvailabilityHeading; loads: boolean } {
  if (input.canSelectRd) {
    return {
      heading: "selector",
      loads: input.rdId !== undefined || input.holdsOwnHours,
    };
  }

  return { heading: input.rdId === undefined ? "greeting" : "named", loads: true };
}
